import { useEffect, useMemo, useState } from 'react';
import type { DeploymentRecord } from '@/services/deploymentService';
import {
  getFailure,
  onFailuresChange,
  requestFailureDetails,
  requestFailures,
  type FailureInfo,
} from '@/utils/deployFailureStore';

export type { FailureInfo };

/**
 * Why each of the failed runs given failed, by deployment id.
 *
 * Pass only the rows on screen. A row the pipeline stamped with its error is
 * answered at once, with no log read. The rest are read from the log, up to
 * 100 runs to a read, and kept for the session, so a row is read once however
 * often it is shown.
 *
 * `opened` says which rows have their details panel open. Only those get the
 * whole error text read for a stamped row.
 */
export function useDeployFailures(
  records: DeploymentRecord[],
  opened?: Record<string, boolean>
): Record<string, FailureInfo | undefined> {
  const [version, setVersion] = useState(0);
  // A record that gains its stamped error must be looked at again.
  const signature = records.map((r) => `${r.deploymentId}:${r.errorSummary ? 1 : 0}:${r.endedAt || ''}`).join(',');
  const openedIds = opened ? records.filter((r) => opened[r.deploymentId]).map((r) => r.deploymentId) : [];
  const openedSignature = openedIds.join(',');

  useEffect(() => onFailuresChange(() => setVersion((v) => v + 1)), []);

  useEffect(() => {
    if (records.length > 0) requestFailures(records);
  }, [signature]);

  useEffect(() => {
    if (openedIds.length === 0) return;
    const wanted = new Set(openedIds);
    requestFailureDetails(records.filter((r) => wanted.has(r.deploymentId)));
  }, [signature, openedSignature]);

  return useMemo(() => {
    const byId: Record<string, FailureInfo | undefined> = {};
    for (const record of records) byId[record.deploymentId] = getFailure(record.deploymentId);
    return byId;
  }, [signature, version]);
}
